import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogProps,
  IconButton,
  Typography,
} from '@mui/material';
import React, { forwardRef, useImperativeHandle } from 'react';

import { useLightbox as useLightboxHook } from '../hooks/useLightbox';

export interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

export interface LightboxProps extends Omit<DialogProps, 'open' | 'onClose'> {
  open: boolean;
  onClose: () => void;
  image: LightboxImage | null;
}

export const Lightbox: React.FC<LightboxProps> = ({
  open,
  onClose,
  image,
  ...props
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          boxShadow: 'none',
        },
      }}
      {...props}
    >
      <Box sx={{ position: 'relative', p: 2 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>
        {image && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={image.src}
              alt={image.alt}
              sx={{
                maxWidth: '100%',
                maxHeight: '85vh',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
            {image.caption && (
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  textAlign: 'center',
                  fontStyle: 'italic',
                }}
              >
                {image.caption}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export interface LightboxContainerRef {
  openLightbox: (image: LightboxImage) => void;
}

export interface LightboxContainerProps
  extends Omit<DialogProps, 'open' | 'onClose' | 'image'> {}

/**
 * Self-contained Lightbox component that manages its own state.
 * Use a ref to get the openLightbox function - no need to pass closeLightbox!
 *
 * @example
 * const lightboxRef = useRef<LightboxContainerRef>(null);
 * <LightboxContainer ref={lightboxRef} />
 * lightboxRef.current?.openLightbox({ src: '...', alt: '...', caption: '...' });
 */
export const LightboxContainer = forwardRef<
  LightboxContainerRef,
  LightboxContainerProps
>((props, ref) => {
  const { open, image, openLightbox, closeLightbox } = useLightboxHook();

  useImperativeHandle(ref, () => ({
    openLightbox,
  }));

  return (
    <Lightbox open={open} onClose={closeLightbox} image={image} {...props} />
  );
});

LightboxContainer.displayName = 'LightboxContainer';

export default Lightbox;
